/**
 * Frontend Logger for React Native
 *
 * Provides consistent, centralized logging across the app.
 * Automatically disabled in production builds (__DEV__ === false).
 *
 * Usage:
 *   import logger from '../util/logger';
 *
 *   logger.info('User logged in', { userId: '123' });
 *   logger.warn('Slow API response', { endpoint: '/reports', duration: 5000 });
 *   logger.error('Failed to fetch data', { error: err.message });
 *   logger.debug('Component mounted', { props: { id: 1 } });
 *
 * Module-specific logging:
 *   const log = logger.module('ImagePicker');
 *   log.info('Image selected', { uri: 'file://...' });
 *   // Output: [ImagePicker] Image selected { uri: 'file://...' }
 */

const LOG_LEVELS = {
  DEBUG: 0,
  INFO: 1,
  WARN: 2,
  ERROR: 3,
  NONE: 4,
};

class Logger {
  constructor(moduleName = null) {
    this.moduleName = moduleName;
    this.minLevel = LOG_LEVELS.DEBUG;
    this.enabled = typeof __DEV__ !== 'undefined' ? __DEV__ : true;
  }

  /**
   * Set the minimum log level
   * @param {'DEBUG' | 'INFO' | 'WARN' | 'ERROR' | 'NONE'} level
   */
  setLevel(level) {
    if (LOG_LEVELS[level] !== undefined) {
      this.minLevel = LOG_LEVELS[level];
    }
    return this;
  }

  /**
   * Enable or disable logging
   * @param {boolean} enabled
   */
  setEnabled(enabled) {
    this.enabled = enabled;
    return this;
  }

  /**
   * Create a module-specific logger
   * @param {string} moduleName - Name of the module for log prefixing
   * @returns {Logger} New logger instance with module context
   */
  module(moduleName) {
    const moduleLogger = new Logger(moduleName);
    moduleLogger.minLevel = this.minLevel;
    moduleLogger.enabled = this.enabled;
    return moduleLogger;
  }

  _formatPrefix() {
    const timestamp = new Date().toISOString().split('T')[1].slice(0, 8);
    return this.moduleName ? `[${timestamp}] [${this.moduleName}]` : `[${timestamp}]`;
  }

  _log(level, levelName, message, data) {
    if (!this.enabled || level < this.minLevel) return;

    const prefix = this._formatPrefix();
    const formattedMessage = `${prefix} ${message}`;

    switch (levelName) {
      case 'ERROR':
        if (data !== undefined) {
          console.error(formattedMessage, data);
        } else {
          console.error(formattedMessage);
        }
        break;
      case 'WARN':
        if (data !== undefined) {
          console.warn(formattedMessage, data);
        } else {
          console.warn(formattedMessage);
        }
        break;
      default:
        if (data !== undefined) {
          console.log(formattedMessage, data);
        } else {
          console.log(formattedMessage);
        }
    }
  }

  /**
   * Log debug message (verbose, development only)
   */
  debug(message, data) {
    this._log(LOG_LEVELS.DEBUG, 'DEBUG', message, data);
  }

  /**
   * Log info message (general information)
   */
  info(message, data) {
    this._log(LOG_LEVELS.INFO, 'INFO', message, data);
  }

  /**
   * Log warning message (potential issues)
   */
  warn(message, data) {
    this._log(LOG_LEVELS.WARN, 'WARN', message, data);
  }

  /**
   * Log error message (errors and exceptions)
   */
  error(message, data) {
    this._log(LOG_LEVELS.ERROR, 'ERROR', message, data);
  }

  /**
   * Log an error object with stack trace
   * @param {string} message - Description of the error context
   * @param {Error} error - The error object
   * @param {Object} additionalData - Additional context data
   */
  logError(message, error, additionalData = {}) {
    this._log(LOG_LEVELS.ERROR, 'ERROR', message, {
      errorName: error?.name,
      errorMessage: error?.message,
      stack: error?.stack,
      ...additionalData,
    });
  }

  /**
   * Create a timer for performance logging
   * @param {string} label - Label for the timer
   * @returns {{ end: () => number }} Timer object with end() method
   */
  time(label) {
    const start = Date.now();
    return {
      end: () => {
        const duration = Date.now() - start;
        this.debug(`${label} completed`, { durationMs: duration });
        return duration;
      },
    };
  }

  /**
   * Log API request (for debugging network calls)
   */
  logApiRequest(method, endpoint, params) {
    this.debug(`API ${method}`, { endpoint, params });
  }

  /**
   * Log API response (for debugging network calls)
   */
  logApiResponse(method, endpoint, statusCode, duration) {
    const level = statusCode >= 400 ? 'warn' : 'debug';
    this[level](`API ${method} response`, { endpoint, statusCode, durationMs: duration });
  }
}

// Export singleton instance
const logger = new Logger();

// Also export class for testing or custom instances
export { Logger, LOG_LEVELS };
export default logger;
